import { isBefore, parseISO } from 'date-fns';

import { formatHoursMinutesTime, replaceTimeInTimestamp } from 'utils/datetime';

import { EDITOR_TEXTS } from './texts';

/**
 * Returns the HH:mm string to prefill a time field.
 *
 * Priority: actual → planned → '00:00'.
 *
 * @param {object} params
 * @param {string|null} params.actual   - Actual ISO timestamp, if recorded.
 * @param {string|null} params.planned  - Planned ISO timestamp, fallback.
 * @param {string}      params.timezone - IANA timezone identifier.
 * @returns {string} HH:mm string.
 */
export const prefillTime = ({ actual, planned, timezone }) => {
	const source = actual ?? planned;

	if (!source) return '00:00';

	return formatHoursMinutesTime(source, timezone);
};

/**
 * Derives a flat list of field slots for every editable station slot.
 *
 * Each descriptor identifies:
 *  - the station index in the chain
 *  - which work-assignment key (id + version) this field maps to
 *  - whether it controls the fromTimestamp or toTimestamp of that WA
 *
 * @param {object[]} sequences - Work assignment sequences from the API.
 * @param {object[]} stations  - Ordered station chain.
 * @returns {object[]} Flat array of field slots.
 */
export const buildStationTimeSlots = (sequences, stations) => {
	if (!sequences?.length || !stations?.length) return [];

	const slots = [];

	for (const sequence of sequences) {
		const workAssignments = Array.isArray(sequence?.workAssignments)
			? sequence.workAssignments
			: [];

		for (const workAssignment of workAssignments) {
			const fromIndex = stations.findIndex((s) => s.id === workAssignment?.fromStation?.id);
			const toIndex = stations.findLastIndex((s) => s.id === workAssignment?.toStation?.id);

			if (fromIndex < 0 || toIndex < 0) continue;

			slots.push({
				stationIndex: fromIndex,
				position: 'departure',
				waId: workAssignment.id,
				waVersion: workAssignment.version,
				waFromTimestamp: workAssignment.fromTimestamp,
				waToTimestamp: workAssignment.toTimestamp,
			});
			slots.push({
				stationIndex: toIndex,
				position: 'arrival',
				waId: workAssignment.id,
				waVersion: workAssignment.version,
				waFromTimestamp: workAssignment.fromTimestamp,
				waToTimestamp: workAssignment.toTimestamp,
			});
		}
	}

	const seen = new Set();

	return slots.filter(({ stationIndex, position }) => {
		const key = `${stationIndex}:${position}`;

		if (seen.has(key)) return false;

		seen.add(key);

		return true;
	});
};

/**
 * Returns the initial form values — a map of `${stationIndex}:departure` /
 * `${stationIndex}:arrival` to HH:mm strings.
 *
 * @param {object}   params
 * @param {object[]} params.stationTimeSlots - Output of `buildStationTimeSlots`.
 * @param {object[]} params.stations         - Ordered station chain.
 * @param {object[]} params.stationActuals   - Per-station actual arrival/departure data.
 * @returns {Record<string, string>} Field key → HH:mm map.
 */
export const buildInitialValues = ({ stationTimeSlots, stations, stationActuals }) =>
	stationTimeSlots.reduce((values, { stationIndex, position, waFromTimestamp, waToTimestamp }) => {
		const station = stations[stationIndex];

		if (!station) return values;

		const timezone = station.timezone;
		const isFirst = stationIndex === 0;
		const isLast = stationIndex === stations.length - 1;

		if (position === 'departure' && !isLast) {
			const actual = stationActuals[stationIndex]?.actualDeparture ?? null;
			const planned = waFromTimestamp ?? station.departure ?? null;
			values[`${stationIndex}:departure`] = prefillTime({ actual, planned, timezone });
		}

		if (position === 'arrival' && !isFirst) {
			const actual = stationActuals[stationIndex]?.actualArrival ?? null;
			const planned = waToTimestamp ?? station.arrival ?? null;
			values[`${stationIndex}:arrival`] = prefillTime({ actual, planned, timezone });
		}

		return values;
	}, {});

// Combines each HH:mm form value with its planned ISO anchor to produce a full UTC
// ISO timestamp. Using a full timestamp means comparisons are date-aware — no
// midnight-crossing heuristics needed.
const buildFullTimestamps = ({ values, stations, stationTimeSlots }) => {
	const fullTimestamps = {};

	for (const { stationIndex, position, waFromTimestamp, waToTimestamp } of stationTimeSlots) {
		const fieldKey = `${stationIndex}:${position}`;
		const time = values[fieldKey];

		if (!time) continue;

		const station = stations[stationIndex];
		if (!station) continue;

		const referenceIso = position === 'departure' ? waFromTimestamp : waToTimestamp;
		if (!referenceIso) continue;

		fullTimestamps[fieldKey] = replaceTimeInTimestamp({
			referenceIso,
			newTime: time,
			timezone: station.timezone ?? 'UTC',
		});
	}

	return fullTimestamps;
};

const checkWithinStation = ({ index, stations, fieldSet, fullTimestamps }) => {
	const arrivalKey = `${index}:arrival`;
	const departureKey = `${index}:departure`;

	if (!fieldSet.has(arrivalKey) || !fieldSet.has(departureKey)) return null;

	const arrivalIso = fullTimestamps[arrivalKey];
	const departureIso = fullTimestamps[departureKey];

	if (!arrivalIso || !departureIso) return null;

	if (isBefore(parseISO(departureIso), parseISO(arrivalIso))) {
		return {
			fieldKey: departureKey,
			message: EDITOR_TEXTS.timeOrderErrorMessage(stations[index]?.code, stations[index]?.code),
		};
	}

	return null;
};

const checkBetweenStations = ({ index, stations, fieldSet, fullTimestamps }) => {
	if (index >= stations.length - 1) return null;

	const departureKey = `${index}:departure`;
	const nextArrivalKey = `${index + 1}:arrival`;

	if (!fieldSet.has(departureKey) || !fieldSet.has(nextArrivalKey)) return null;

	const departureIso = fullTimestamps[departureKey];
	const nextArrivalIso = fullTimestamps[nextArrivalKey];

	if (!departureIso || !nextArrivalIso) return null;

	if (isBefore(parseISO(nextArrivalIso), parseISO(departureIso))) {
		return {
			fieldKey: departureKey,
			message: EDITOR_TEXTS.timeOrderErrorMessage(stations[index]?.code, stations[index + 1]?.code),
		};
	}

	return null;
};

/**
 * Validates chronological ordering across the station chain.
 *
 * Returns `null` when valid, or `{ fieldKey, message }` for the first violation found.
 *
 * @param {object}              params
 * @param {Record<string, string>} params.values        - Current form values (field key → HH:mm).
 * @param {object[]}            params.stations         - Ordered station chain.
 * @param {object[]}            params.stationTimeSlots - Output of `buildStationTimeSlots`.
 * @returns {{ fieldKey: string, message: string } | null}
 */
export const validateChain = ({ values, stations, stationTimeSlots }) => {
	const fullTimestamps = buildFullTimestamps({ values, stations, stationTimeSlots });
	const fieldSet = new Set(
		stationTimeSlots.map(({ stationIndex, position }) => `${stationIndex}:${position}`)
	);

	for (let i = 0; i < stations.length; i++) {
		const withinError = checkWithinStation({ index: i, stations, fieldSet, fullTimestamps });

		if (withinError) return withinError;

		const betweenError = checkBetweenStations({ index: i, stations, fieldSet, fullTimestamps });

		if (betweenError) return betweenError;
	}

	return null;
};

/**
 * Builds the `timeRangeInputs` array for the mutation from current form values.
 *
 * Returns an empty array when no editable fields have values.
 *
 * @param {object}              params
 * @param {object[]}            params.stationTimeSlots - Output of `buildStationTimeSlots`.
 * @param {object[]}            params.stations         - Ordered station chain.
 * @param {Record<string, string>} params.values        - Current form values (field key → HH:mm).
 * @returns {object[]} Array of `{ key, value }` timeRangeInput objects.
 */
export const buildTimeRangeInputs = ({ stationTimeSlots, stations, values }) => {
	const waMap = new Map();

	for (const slot of stationTimeSlots) {
		const { stationIndex, position, waId, waVersion, waFromTimestamp, waToTimestamp } = slot;
		const station = stations[stationIndex];

		if (!station) continue;

		const newTime = values[`${stationIndex}:${position}`];

		if (!newTime) continue;

		const referenceIso =
			position === 'departure'
				? (waFromTimestamp ?? station.departure)
				: (waToTimestamp ?? station.arrival);

		if (!referenceIso) continue;

		const newIso = replaceTimeInTimestamp({
			referenceIso,
			newTime,
			timezone: station.timezone ?? 'UTC',
		});

		if (!waMap.has(waId)) {
			waMap.set(waId, {
				key: { id: waId, version: waVersion },
				value: { fromTimestamp: waFromTimestamp, toTimestamp: waToTimestamp },
			});
		}

		const entry = waMap.get(waId);

		if (position === 'departure') entry.value.fromTimestamp = newIso;
		else entry.value.toTimestamp = newIso;
	}

	return Array.from(waMap.values());
};
